import React, { useEffect, useLayoutEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { HelmetProvider } from 'react-helmet-async';
import './App.css';
import HomePage from './components/HomePage';
import ProductPage from './components/ProductPage';
import ProductsPage from './components/ProductsPage';
import AboutPage from './components/AboutPage';
import BlogPage from './components/BlogPage';
import BlogPostPage from './components/BlogPostPage';
import ContactPage from './components/ContactPage';
import FAQsPage from './components/FAQsPage';
import TechnologyPage from './components/TechnologyPage';
import PeptidesInfoPage from './components/PeptidesInfoPage';
import CartPage from './components/CartPage';
import CheckoutPage from './components/CheckoutPage';
import CheckoutSuccessPage from './components/CheckoutSuccessPage';
import AdminPage from './components/AdminPage';
import AdminSEOPage from './components/AdminSEOPage';
import StatusPage from './components/StatusPage';
import SubscriptionsLandingPage from './components/SubscriptionsLandingPage';
import AuthPage from './components/AuthPage';
import AuthCallback from './components/AuthCallback';
import ProfilePage from './components/ProfilePage';
import WishlistPage from './components/WishlistPage';
import RecommendationsPage from './components/RecommendationsPage';
import OrderHistoryPage from './components/OrderHistoryPage';
import TermsPage from './components/TermsPage';
import PrivacyPage from './components/PrivacyPage';
import ReturnPolicyPage from './components/ReturnPolicyPage';
import ShippingPolicyPage from './components/ShippingPolicyPage';
import CookiePolicyPage from './components/CookiePolicyPage';
import DistributorPage from './components/DistributorPage';
import AffiliatesPage from './components/AffiliatesPage';
import AffiliateLoginPage from './components/AffiliateLoginPage';
import AffiliateDashboardPage from './components/AffiliateDashboardPage';
import AffiliateSetupPasswordPage from './components/AffiliateSetupPasswordPage';
import AffiliateMarketingKitPage from './components/AffiliateMarketingKitPage';
import AnalyticsTracker from './components/AnalyticsTracker';
import YandexMetrica from './components/YandexMetrica';
import ReviewSubmitPage from './components/ReviewSubmitPage';
import ReconstitutionCalculatorPage from './components/ReconstitutionCalculatorPage';
import BulkPage from './components/BulkPage';
import NotFoundPage from './components/NotFoundPage';
import { LanguageProvider } from './contexts/LanguageContext';
import { CurrencyProvider } from './contexts/CurrencyContext';
import { CartProvider } from './contexts/CartContext';
import { AuthProvider } from './contexts/AuthContext';
import { WishlistProvider } from './contexts/WishlistContext';
import { GeoCountryProvider } from './contexts/GeoCountryContext';
import { Toaster } from './components/ui/toaster';
import { Toaster as SonnerToaster } from './components/ui/sonner';
import InactivityLogout from './components/InactivityLogout';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// ScrollToTop component - scrolls to top on route change.
//
// Uses `useLayoutEffect` so the scroll happens BEFORE React paints the new
// page. On mobile (especially iOS Safari) a plain `useEffect` would let the
// browser commit a new render at the previous scroll position first, which
// is what made every navigation feel like it "opened in the middle of the
// page". Also resets both `documentElement` and `body` because mobile
// Safari sometimes scrolls only one of them depending on layout.
// Hash anchors (e.g. `/faqs#shipping`) are honored so deep links still work.
function ScrollToTop() {
  const { pathname, hash } = useLocation();

  useLayoutEffect(() => {
    if (hash) {
      // Defer to a microtask so the target element exists in the DOM.
      requestAnimationFrame(() => {
        const el = document.getElementById(hash.slice(1));
        if (el) {
          el.scrollIntoView({ block: 'start', behavior: 'instant' });
          return;
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      });
      return;
    }
    // Reset every scroll container, iOS Safari is inconsistent about
    // which one is the active viewport scroller.
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash]);

  return null;
}

// ReferralTracker - captures ?ref=PROMOCODE on any page load and stores attribution
function ReferralTracker() {
  const location = useLocation();
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    if (ref) {
      const code = ref.toUpperCase().trim();
      try {
        // Persist locally (90 days) and notify backend to set httpOnly cookie + count click
        const expires = new Date(Date.now() + 90 * 24 * 3600 * 1000).toUTCString();
        document.cookie = `plp_ref=${code}; expires=${expires}; path=/; SameSite=Lax`;
        localStorage.setItem('plp_ref', code);
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/affiliate/track-click/${encodeURIComponent(code)}`;
        fetch(url, { method: 'POST', credentials: 'include' }).catch(() => {});
      } catch {}
    }
  }, [location.search]);
  return null;
}

// Supported language prefixes. Keep in sync with LanguageContext SUPPORTED.
const LANG_PREFIXES = ['en', 'es', 'ru', 'de', 'fr', 'it', 'pl', 'nl', 'pt'];

// Single source of truth for every page-level route. We render each entry
// twice: once at its bare path (`/products`) and once prefixed with a
// language code (`/:lang/products`). Both forms render the same component;
// LanguageContext picks the active language up from the URL prefix.
const PAGE_ROUTES = [
  { path: '',                             element: <HomePage /> },
  { path: '/',                            element: <HomePage /> },
  { path: '/products',                    element: <ProductsPage /> },
  { path: '/product/:id',                 element: <ProductPage /> },
  { path: '/about',                       element: <AboutPage /> },
  { path: '/blog',                        element: <BlogPage /> },
  { path: '/blog/:slug',                  element: <BlogPostPage /> },
  { path: '/contact',                     element: <ContactPage /> },
  { path: '/faqs',                        element: <FAQsPage /> },
  { path: '/technology',                  element: <TechnologyPage /> },
  { path: '/peptides',                    element: <PeptidesInfoPage /> },
  { path: '/cart',                        element: <CartPage /> },
  { path: '/checkout',                    element: <CheckoutPage /> },
  { path: '/checkout/success',            element: <CheckoutSuccessPage /> },
  { path: '/auth',                        element: <AuthPage /> },
  { path: '/login',                       element: <AuthPage /> },
  { path: '/register',                    element: <AuthPage /> },
  { path: '/auth/callback',               element: <AuthCallback /> },
  { path: '/profile',                     element: <ProfilePage /> },
  { path: '/wishlist',                    element: <WishlistPage /> },
  { path: '/recommendations',             element: <RecommendationsPage /> },
  { path: '/orders',                      element: <OrderHistoryPage /> },
  { path: '/admin',                       element: <AdminPage /> },
  { path: '/admin/seo',                   element: <AdminSEOPage /> },
  { path: '/review/:token',               element: <ReviewSubmitPage /> },
  { path: '/uptime',                      element: <StatusPage /> },
  { path: '/subscriptions',               element: <SubscriptionsLandingPage /> },
  { path: '/terms',                       element: <TermsPage /> },
  { path: '/privacy',                     element: <PrivacyPage /> },
  { path: '/return-policy',               element: <ReturnPolicyPage /> },
  { path: '/shipping-policy',             element: <ShippingPolicyPage /> },
  { path: '/cookie-policy',               element: <CookiePolicyPage /> },
  { path: '/distributor',                 element: <DistributorPage /> },
  { path: '/affiliates',                  element: <AffiliatesPage /> },
  { path: '/affiliate/login',             element: <AffiliateLoginPage /> },
  { path: '/affiliate/setup-password',    element: <AffiliateSetupPasswordPage /> },
  { path: '/affiliate/reset-password',    element: <AffiliateSetupPasswordPage /> },
  { path: '/affiliate/dashboard',         element: <AffiliateDashboardPage /> },
  { path: '/affiliate/marketing',         element: <AffiliateMarketingKitPage /> },
  { path: '/calculators/reconstitution',  element: <ReconstitutionCalculatorPage /> },
  { path: '/bulk',                        element: <BulkPage /> },
];

// Router component to handle OAuth callback detection
function AppRouter() {
  const location = useLocation();
  
  // Check URL fragment for session_id (OAuth callback)
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      {/* Bare routes, backward-compatible for existing links + ?lang= flow */}
      {PAGE_ROUTES.map((r) => (
        <Route key={`bare-${r.path}`} path={r.path} element={r.element} />
      ))}
      {/* Language-prefixed routes, /en/products, /es/about etc. for SEO */}
      {LANG_PREFIXES.map((lang) =>
        PAGE_ROUTES.map((r) => (
          <Route
            key={`${lang}-${r.path}`}
            path={`/${lang}${r.path === '/' || r.path === '' ? '' : r.path}`}
            element={r.element}
          />
        ))
      )}
      {/* Catch-all 404, emits <meta name="robots" content="noindex,follow">
          so GSC stops listing soft-404 URLs like /{lang}/compare/X-vs-Y,
          /{lang}/products/{slug}, and /{lang}/faq/{slug} under "Alternate
          page with proper canonical tag". MUST be the last <Route>. */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
}

function App() {
  // Landing page removed per user request

  return (
    <HelmetProvider>
      <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
        <GeoCountryProvider>
        <BrowserRouter>
          <ScrollToTop />
          <ReferralTracker />
          <AnalyticsTracker />
          <YandexMetrica />
          <LanguageProvider>
            <CurrencyProvider>
              <AuthProvider>
                <CartProvider>
                  <WishlistProvider>
                    <div className="App">
                      <AppRouter />
                      <InactivityLogout />
                      <Toaster />
                      <SonnerToaster />
                    </div>
                  </WishlistProvider>
                </CartProvider>
              </AuthProvider>
            </CurrencyProvider>
          </LanguageProvider>
        </BrowserRouter>
        </GeoCountryProvider>
      </GoogleOAuthProvider>
    </HelmetProvider>
  );
}

export default App;